import { X } from 'lucide-react';
import { Flag } from '@/components/ui/flag/flag';
import type { RideEntry, RideTimelineEntry, RideTimelineStatus } from '../types';

function getRideStatus(ride: RideEntry, effectiveNowMs: number): RideTimelineStatus {
  if (effectiveNowMs >= ride.endUtcMs) return 'completed';
  if (effectiveNowMs >= ride.startUtcMs) return 'live';
  return 'upcoming';
}

export function RideCascadePanel({
  entry,
  effectiveNowMs,
  onClose,
}: {
  entry: RideTimelineEntry;
  effectiveNowMs: number;
  onClose: () => void;
}) {
  return (
    <div className="mt-8 overflow-hidden rounded-2xl border border-dgr-gold/40 bg-white/80 shadow-lg shadow-dgr-gold/10 backdrop-blur">
      <div className="flex items-center justify-between gap-3 border-b border-stone-950/10 px-5 py-3">
        <div className="flex items-center gap-3">
          <Flag code={entry.countryCode} size="sm" className="rounded-sm shadow" />
          <div>
            <p className="text-sm font-semibold text-stone-950">{entry.countryName}</p>
            <p className="text-[11px] text-stone-600">
              {entry.rideCount} {entry.rideCount === 1 ? 'ride' : 'rides'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-1 text-stone-600 transition-colors hover:bg-stone-950/10 hover:text-stone-950"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="grid gap-1.5 px-5 py-4 sm:grid-cols-2 lg:grid-cols-3">
        {entry.rides.map((r, idx) => {
          const status = getRideStatus(r, effectiveNowMs);
          const rideDot =
            status === 'live'
              ? 'bg-dgr-gold'
              : status === 'completed'
                ? 'bg-stone-400'
                : 'border border-stone-950/30';
          return (
            <li
              key={r.id}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-xs transition-all duration-300 animate-in fade-in slide-in-from-top-1 ${
                status === 'live' ? 'bg-dgr-gold/15 ring-1 ring-dgr-gold/40' : 'bg-stone-950/[0.03]'
              }`}
              style={{ animationDelay: idx < 8 ? `${idx * 25}ms` : undefined }}
            >
              <span className={`h-2 w-2 shrink-0 rounded-full ${rideDot}`} />
              <div className="flex flex-1 flex-col truncate">
                <span className="truncate text-stone-900">{r.city}</span>
                <span className="text-[10px] text-stone-500">{r.participantsLabel} riders</span>
              </div>
              <span className="flex flex-col items-end leading-tight">
                <span className="font-mono text-[10px] tracking-wider text-stone-500">
                  {r.utcLabel} UTC
                </span>
                <span className="font-mono text-[9px] tracking-wider text-stone-400">
                  {r.localLabel} local
                </span>
              </span>
              <span
                className={`text-[10px] uppercase tracking-wider ${
                  status === 'live'
                    ? 'text-dgr-gold-hover'
                    : status === 'completed'
                      ? 'text-stone-500'
                      : 'text-stone-600'
                }`}
              >
                {status === 'live' ? 'Riding' : status === 'completed' ? 'Done' : 'Soon'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
